/** Undertaking types, constants, and re-exports (PSM from util/rpwTypes). Used by Undertaking.tsx, UndertakingCards, UndertakingEmailModal, legacyDocumentAutomation. */

import type { PSM } from "../../util/rpwTypes";

export type { PSM };

/** Parsed undertaking defect from XML; used by Undertaking.tsx (readUndertakingsFromXml). */
export type UndertakingXmlItem = {
    xmlIndex: number;
    key: string;
    /** XML DefectId when present; aligns with undertaking save payload `id` (see UndertakingFuncs UndertakingJson). */
    defectId?: string; // used in undertaking, system task creation and undertaking dashboard lookup
    /** Set when undertaking is enabled; first follow-up due = anchor + first offset (see undertakingTasks). */
    followUpAnchorDate?: string;
    /** User-overridden due (YYYY-MM-DD) from Defect `UndertakingTaskDue1st` — see UndertakingTaskCustomizstion. */
    persistedUndertakingDue1st?: string;
    /** Defect `UndertakingTaskDue2nd`. */
    persistedUndertakingDue2nd?: string;
    /** Defect `UndertakingTaskDueComply`. */
    persistedUndertakingDueComply?: string;
    typeRaw?: string;
    title?: string;
    summary?: string;
    instrumentNumber?: string;
    lender?: string;
    lenderEmail?: string;
    amount?: string;
    isActiveTab: boolean;
    vendor: boolean;
    vendorLawyer: boolean;
    completed: boolean;
    completedDate?: Date | null;
    completedNumber?: string;
    lastFollowUpDate?: Date | null;
    lastFollowUpBy?: string;
    /** `Data/@UndertakingAutoSendEmail`: absent or `"1"` = on; `"0"` = off. */
    autoSendEmailEnabled?: boolean;
};

/** `Data` attribute for per–mortgage-discharge auto email toggle (Undertaking dashboard). */
export const UNDERTAKING_XML_ATTR_AUTO_SEND_EMAIL = "UndertakingAutoSendEmail";

/** Stable segment in undertaking-linked file task notes: XML DefectId when non-empty, else synthetic `key`. */
export function undertakingTaskLinkKey(u: Pick<UndertakingXmlItem, "defectId" | "key">): string {
    const id = (u.defectId ?? "").trim();
    return id.length > 0 ? id : u.key; 
}

/** Email draft for Undertaking follow-up modal. No shared email-draft type in codebase; MailtoArgs is for mailto: only; EmailBody (MortgageAnalyzer) has different shape. Centralized from UndertakingEmailModal. */
export type UndertakingEmailDraft = {
    contextLabel: string;
    to: string;
    subject: string;
    body: string;
    // Initial values from Undertaking Preferences (editable per-email).
    sendCopy: boolean;
    bccLawyer: boolean;
    ccOtherSide: boolean; // Render only if applicable
    // Email addresses used by toggles (best-effort).
    copyTo?: string;
    lawyerEmail?: string;
    otherSideFirmContactEmail?: string;
};

/** Identity for legacy doc automation. Shared with: legacyDocumentAutomation.ts (LegacyDocAutomationBaseOpts). */
export type LegacyDocTypeIdentity = {
    doctypeId: string;
    groupingId?: string;
    expectedAppId?: string;
};

/** Undertaking doc type entry: identity + UI key/label. Reused for Generate, Open, Email. */
export type UndertakingLegacyDocType = LegacyDocTypeIdentity & {
    key: string; // eeva: if pick partial, there could be type error, may need :?
    label: string;
};

/** Reused from: UndertakingCards.tsx (centralized here). Used by: UndertakingCards, Undertaking.tsx, mapUndertakingXmlItemToIssue. */
export type UndertakingIssue = {
    key: string;
    /** XML `DefectId` on the defect node (may be empty). File tasks use `undertakingTaskLinkKey` → `taskNotesKey`. */
    defectId?: string;
    /** Matches `UNDERTAKING_TASK:` segment in file task notes (DefectId when set, else `key`). */
    taskNotesKey: string;
    title: string;
    summary: string;
    xmlIndex: number;
    instrumentNumber?: string;
    lender?: string;
    lenderEmail?: string;
    escalationEmail?: string;
    dueDate?: Date | null;
    lastFollowUpDate?: Date | null;
    lastFollowUpBy?: string;
    completed: boolean;
    completedDate?: Date | null;
    completedNumber?: string;
    /** Per defect: automatic follow-up email (see `UNDERTAKING_XML_ATTR_AUTO_SEND_EMAIL` on `Data`). */
    autoSendEmailEnabled: boolean;
};

export type UndertakingItemType = {
    id: string;
    name: string;
    desc?: string;
    Date1: string;
    Date2: string;
    CustomDate1: string;
    CustomDate2: string;
    SentDate1: string;
    SentDate2: string;
    SentBy1: any;
    SentBy2: any;
    CanAutoFollowUp: boolean;
    CompletedDate?: string;
    emailOk?: boolean;
}

/** Derived from UndertakingIssue. Used by: UndertakingCards (onEdit), Undertaking.tsx (handleEdit, openSpecificUndertakingIssueFromIssues). */
export type UndertakingIssueEditPayload = Pick<
    UndertakingIssue,
    "xmlIndex" | "instrumentNumber" | "defectId"
>;
/** Derived from UndertakingIssue. Used by: UndertakingCards (onReminder), Undertaking.tsx (handleReminder). */
export type UndertakingIssueReminderPayload = Pick<UndertakingIssue, "title">;

/** Reused from: Undertaking.tsx (was local DocumentRow; centralized here). */
export type UndertakingDocumentRow = { name: string, id: string};

/** Reused from: UndertakingEmailModal.tsx (centralized here). Used by: UndertakingEmailModal (mailtoHref). */
export type MailtoArgs = {
    to: string;
    cc?: string[];
    bcc?: string[];
    subject: string;
    body: string;
};

/** Reused from: UndertakingCards.tsx (centralized here). Used by: UndertakingCards, Undertaking.tsx. */
export type UndertakingPanelMode = "active" | "completed";

/** Label for completed undertaking: "notified" (Sale mortgage) vs "complied". Used by: UndertakingCards. Display: capitalize when rendering. */
export type UndertakingCompletedLabel = "notified" | "complied";

/** Label for the completed instrument number in the UI. Used by: UndertakingCards. */
export const UNDERTAKING_COMPLETED_NUMBER_LABEL = "Registered Security Discharge #";

/** File type for Undertaking: use PSM (re-exported above from util/rpwTypes). Use PSM | '' when value may be empty. */

/** New for Undertaking. Used by: Undertaking.tsx (selectNodesCompat, readUndertakingsFromXml, XML helpers). */
export interface XmlNodeLike {
    getAttribute?(name: string): string | null;
    setAttribute?(name: string, value: string): void;
    selectSingleNode?(xpath: string): XmlNodeLike | null;
    ownerDocument?: Document | XmlNodeLike;
}


/** XML attribute names below are constrained by legacy XML; casing and spelling must match the document. Do not change; do not adjust to lowercase or capitalize. */
/** Extracted from: Undertaking.tsx (readUndertakingsFromXml). Used by: Undertaking.tsx (readOneDefectToXmlItem). */
export const UNDERTAKING_XML_ATTRS_DEFECT_NAME = [
    "DisplayName",
    "FullName",
    "ShortNameScreenPrint",
    "name",
    "defaultName",
] as const;
/** Extracted from: Undertaking.tsx (readUndertakingsFromXml). */
export const UNDERTAKING_XML_ATTRS_SHORT_DESC = [
    "ShortDescription",
    "Summary",
    "StatusText",
] as const;
/** Extracted from: Undertaking.tsx (readUndertakingsFromXml). */
export const UNDERTAKING_XML_ATTRS_INSTRUMENT = [
    "InstrumentNumber",
    "TeranetInstrumentNum",
    "RegistrationNumber",
] as const;
/** Extracted from: Undertaking.tsx (readUndertakingsFromXml). */
export const UNDERTAKING_XML_ATTRS_LENDER = ["Lender", "LenderName"] as const;
/** Extracted from: Undertaking.tsx (readUndertakingsFromXml). */
export const UNDERTAKING_XML_ATTRS_EMAIL = [
    "Email",
    "FirmEmail",
    "EmailAddress",
    "ContactEmail",
] as const;
/** Extracted from: Undertaking.tsx (readUndertakingsFromXml). */
export const UNDERTAKING_XML_ATTRS_AMOUNT = [
    "Amount",
    "MortgageAmount",
    "PrincipalAmount",
] as const;
/** Extracted from: Undertaking.tsx (readUndertakingsFromXml). */
export const UNDERTAKING_XML_ATTRS_FOLLOW_UP_DATE = [
    "LastFollowUp",
    "LastFollowUpDate",
    "FollowUp",
    "FollowUpDate",
] as const;
/** Extracted from: Undertaking.tsx (readUndertakingsFromXml). */
export const UNDERTAKING_XML_ATTRS_FOLLOW_UP_BY = [
    "FollowUpBy",
    "FollowUpUser",
    "LastFollowUpBy",
    "LastFollowUpUser",
] as const;
/** Extracted from: Undertaking.tsx (readUndertakingsFromXml). */
export const UNDERTAKING_XML_ATTRS_COMPLETED_FLAG = [
    "CheckResolved",
    "checkResolved",
] as const;
/** Extracted from: Undertaking.tsx (readUndertakingsFromXml). */
export const UNDERTAKING_XML_ATTRS_COMPLETED_DATE = [
    "DateCompliedWith",
    "CompletedDate",
    "ResolvedDate",
] as const;
/** Extracted from: Undertaking.tsx (readUndertakingsFromXml). */
export const UNDERTAKING_XML_ATTRS_COMPLETED_NUMBER = [
    "TeranetInstrumentNum",
    "RegistrationNumber",
    "InstrumentNumber",
] as const;

/** Used by: Undertaking.tsx (building activeIssues/completedIssues from readUndertakingsFromXml). */
export function mapUndertakingXmlItemToIssue(args: {
    item: UndertakingXmlItem;
    title: string;
    summary: string;
    dueDate: Date | null;
}): UndertakingIssue {
    const u = args.item;
    return {
        key: u.key,
        defectId: u.defectId,
        taskNotesKey: undertakingTaskLinkKey(u),
        title: args.title,
        summary: args.summary,
        xmlIndex: u.xmlIndex,
        instrumentNumber: u.instrumentNumber,
        lender: u.lender,
        lenderEmail: u.lenderEmail,
        dueDate: args.dueDate,
        lastFollowUpDate: u.lastFollowUpDate ?? null,
        lastFollowUpBy: u.lastFollowUpBy,
        completed: u.completed,
        completedDate: u.completedDate ?? null,
        completedNumber: u.completedNumber,
        autoSendEmailEnabled: u.autoSendEmailEnabled !== false,
    };
}

/** Shared grouping ID for legacy document list (e.g. "By Name" view). Avoids repeating the same string in UNDERTAKING_LEGACY_DOC_TYPES. */
export const LEGACY_DOC_GROUPING_BY_NAME = "documentListGroupingMethod2";

/** Centralized mapping for Undertaking legacy doc types. Used by: Undertaking.tsx (LEGACY_DOCUMENT_GENERATE, Generate/Open/Email). */
export const UNDERTAKING_LEGACY_DOC_TYPES = {
    VendorSolicitor: {
        key: "UNDERTAKING_VENDOR_SOLICITOR",
        label: "Undertaking - Vendor’s Solicitor’s",
        doctypeId: "626",
        groupingId: LEGACY_DOC_GROUPING_BY_NAME,
    },
    DischargeUndertaking: {
        key: "UNDERTAKING_DISCHARGE",
        label: "Discharge - Undertaking",
        doctypeId: "2230",
        groupingId: LEGACY_DOC_GROUPING_BY_NAME,
    },
} as const satisfies Record<string, UndertakingLegacyDocType>;

/** Attribute names below are constrained by legacy document shell / DOM; 
 * casing and spelling must match. Do not change; do not adjust to lowercase or capitalize. */
/** Reused from: legacyDocumentAutomation.ts (centralized here). React<->legacy contract; used by legacyDocumentAutomation (hasExistingGeneratedDocFromRow). */
export const LEGACY_DOC_ROW_DOC_ID_ATTRS = [
    "doc_id",
    "docid",
    "DocID",
    "doc_id_current",
    "doc_id_curr",
    "doc_id_latest",
    "generated_doc_id",
] as const;

/** Reused from: legacyDocumentAutomation.ts (centralized here). */
export const LEGACY_DOC_ROW_DOC_NAME_ATTRS = [
    "doc_name",
    "DocName",
    "filename",
    "FileName",
    "doc_filename",
    "merged_doc_name",
    "href",
    "url",
] as const;

/** Reused from: legacyDocumentAutomation.ts (centralized here). */
export const LEGACY_DOC_ROW_DOC_EXISTS_FLAG_ATTRS = [
    "doc_exists",
    "DocExists",
    "has_doc",
    "hasGenerated",
    "isgenerated",
] as const;
